import { useEffect, useState } from 'react';
import { api } from '../api';
import type { NotificationSettingsDto, SpaceDto, UserDto } from '../types';
import { Card, ErrorBanner, Loader } from '../components/common';

interface Props {
  user: UserDto;
  space: SpaceDto;
  spaces: SpaceDto[];
  onSelectSpace: (spaceId: string) => void;
}

const TOGGLES: { key: keyof NotificationSettingsDto; label: string }[] = [
  { key: 'notificationsEnabled', label: 'Уведомления' },
  { key: 'wateringEnabled', label: 'Полив' },
  { key: 'fertilizingEnabled', label: 'Подкормка' },
  { key: 'pruningEnabled', label: 'Обрезка' },
  { key: 'diagnosisFollowupEnabled', label: 'Контроль после лечения' },
  { key: 'dailyDigestEnabled', label: 'Ежедневный дайджест' },
];

export function Settings({ user, space, spaces, onSelectSpace }: Props) {
  const [settings, setSettings] = useState<NotificationSettingsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .settings()
      .then(setSettings)
      .catch((e) => setError(e instanceof Error ? e.message : 'Ошибка загрузки'));
  }, []);

  async function patch(payload: Partial<NotificationSettingsDto>) {
    setBusy(true);
    try {
      setSettings(await api.updateSettings(payload));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorBanner message={error} />;
  if (!settings) return <Loader />;

  return (
    <div>
      <h1>Настройки</h1>

      <Card>
        <div className="title">{[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Профиль'}</div>
        <div className="hint">
          {user.username ? `@${user.username} · ` : ''}
          {user.timezone ?? 'часовой пояс не определён'}
        </div>
      </Card>

      {spaces.length > 1 ? (
        <Card>
          <div className="title">Пространство</div>
          <div className="field mt">
            <select value={space.id} onChange={(e) => onSelectSpace(e.target.value)}>
              {spaces.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({item.plantCount})
                </option>
              ))}
            </select>
          </div>
        </Card>
      ) : null}

      <Card>
        <div className="title">Локация</div>
        <div className="hint mt">
          {space.location
            ? `${space.location.city ?? 'координаты'} · ${space.location.timezone}`
            : 'Не указана — советы с учётом погоды отключены.'}
        </div>
        <div className="hint mt">
          Координаты используются только для прогноза погоды и определения сезона. Изменить город можно
          в боте командой /start.
        </div>
      </Card>

      <Card>
        <div className="title">Уведомления</div>
        {TOGGLES.map((toggle) => (
          <div key={toggle.key} className="switch">
            <span>{toggle.label}</span>
            <input
              type="checkbox"
              disabled={busy}
              checked={Boolean(settings[toggle.key])}
              onChange={(e) => patch({ [toggle.key]: e.target.checked })}
            />
          </div>
        ))}

        <div className="field mt">
          <label>Время уведомлений</label>
          <input
            type="time"
            value={settings.preferredTime}
            disabled={busy}
            onChange={(e) => patch({ preferredTime: e.target.value })}
          />
        </div>

        <div className="row" style={{ gap: 10 }}>
          <div className="field grow">
            <label>Тихие часы с</label>
            <input
              type="time"
              value={settings.quietHoursStart ?? ''}
              disabled={busy}
              onChange={(e) => patch({ quietHoursStart: e.target.value })}
            />
          </div>
          <div className="field grow">
            <label>до</label>
            <input
              type="time"
              value={settings.quietHoursEnd ?? ''}
              disabled={busy}
              onChange={(e) => patch({ quietHoursEnd: e.target.value })}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="title">Данные</div>
        <div className="hint mt">
          Фотографии отправляются AI-провайдеру для анализа и хранятся в истории растения. Оригиналы
          автоматически удаляются по истечении срока хранения. Растение, фото и аккаунт можно удалить.
        </div>
      </Card>

      <SpeciesCredits />
    </div>
  );
}

interface Credit {
  source: string | null;
  author: string;
  license: string;
}

/** Картинки видов — из Викисклада по свободным лицензиям; лицензия требует указать авторов. */
function SpeciesCredits() {
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState<Record<string, Credit> | null>(null);

  useEffect(() => {
    if (!open || credits) return;
    fetch('/species/credits.json')
      .then((r) => r.json())
      .then(setCredits)
      .catch(() => setCredits({}));
  }, [open, credits]);

  return (
    <Card>
      <div className="row between">
        <div className="title">Картинки видов</div>
        <button className="btn secondary small" onClick={() => setOpen((v) => !v)}>
          {open ? 'Скрыть' : 'Авторы'}
        </button>
      </div>
      <div className="hint mt">
        Пока вы не добавили своё фото, показываем картинку вида из Wikimedia Commons (свободные лицензии).
      </div>
      {open && credits
        ? Object.entries(credits).map(([name, credit]) => (
            <div key={name} className="hint mt">
              {name}: {credit.author} ·{' '}
              {credit.source ? (
                <a href={credit.source} target="_blank" rel="noreferrer">
                  {credit.license}
                </a>
              ) : (
                credit.license
              )}
            </div>
          ))
        : null}
    </Card>
  );
}
